import { Link, graphql, useStaticQuery } from "gatsby";
import React, { useMemo } from "react";
import SocialsIcon from "../../../../components/content/Socials/Icon";
import TextInput from "../../../../components/shared/FormComponents/TextInput";
import Button from "../../../../components/shared/Button";
import { GatsbyImage, ImageDataLike, getImage } from "gatsby-plugin-image";
import { Menu } from "../../../layouts/Layout/Footer";
import { ISalesChannel } from "../../../../services/ecommerceApi/SalesChannel/types";

export interface FooterProps {
    salesChannel: ISalesChannel;
    socials: any[];
    logoFile?: {
        node: ImageDataLike;
    };
    menus: Menu[];
}

export default function Footer({ salesChannel, socials, logoFile, menus }: FooterProps) {
    const [email, setEmail] = React.useState("");

    const firstMenu = menus[0];
    const secondMenu = menus[1];

    const data = useStaticQuery(graphql`
        query {
            integration(type: {eq: "klaviyo"}){
                config{
                    property
                    propertyId
                    type
                    value
                }
            }
        }
    `);

    const klaviyoConfig = data?.integration?.config || [];
    const subscribeFormId = klaviyoConfig.find(p => p.property == "subscribeFormId")?.value;

    return (
        <footer className="">
            <section className="bg-secondary-90 text-white pb-8">
                <div className="container max-w-screen-lg mx-auto">
                    <div className="flex flex-wrap -mx-3">
                        <div className="w-full md:w-1/4 px-3 md:order-4">
                            {subscribeFormId && (
                                <>
                                    <h2 className="text-lg uppercase mt-6 mb-3">Join the Club</h2>
                                    <p className="mb-4">
                                        Join thousands of other members and get the latest deals and offers.
                                    </p>
                                    <div className={`klaviyo-form-${subscribeFormId}`}></div>
                                </>
                            )}
                        </div>
                        <div className="w-1/2 md:w-1/4 px-3">
                            {firstMenu && (
                                <>
                                    <h2 className="text-lg uppercase mb-3  mt-6 ">{firstMenu.displayName}</h2>
                                    <ul>
                                        {firstMenu.items.map(menuItem => {

                                            const to = useMemo(() => {
                                                if (menuItem.id) {
                                                    return menuItem.link.src.startsWith("/") ? menuItem.link.src : `/${menuItem.link.src}`;
                                                }
                                                return menuItem.link.src;
                                            }, [menuItem]);

                                            return (
                                                <li key={menuItem.id}>
                                                    <Link className="block py-[8px]" to={to}>{menuItem.name}</Link>
                                                </li>
                                            )
                                        })
                                        }
                                    </ul>
                                </>
                            )}
                        </div>
                        <div className="w-1/2 md:w-1/4 px-3">
                            {secondMenu && (
                                <>
                                    <h2 className="text-lg uppercase mb-3  mt-6 ">{secondMenu.displayName}</h2>
                                    <ul>
                                        {secondMenu.items.map(menuItem => (
                                            <li key={menuItem.id}>
                                                <Link className="block py-[8px]" to={menuItem.link.src}>{menuItem.name}</Link>
                                            </li>
                                        ))}
                                    </ul>
                                </>
                            )}
                        </div>
                        <div className="w-full md:w-1/4 px-3 md:order-3">
                            <h2 className="text-lg uppercase mb-3  mt-6 ">Contact Us</h2>
                            {salesChannel?.address}
                            {salesChannel?.phone && (
                                <div className="mt-3">
                                    <a
                                        className="px-3 py-[5px] uppercase font-bold text-xs bg-white text-secondary inline-block"
                                        href={`tel:${salesChannel.phone}`}
                                    >
                                        {salesChannel.phone}
                                    </a>
                                </div>
                            )}
                            {salesChannel?.email && (
                                <div className="mt-3">
                                    <a
                                        className="px-3 py-[6px] uppercase font-bold text-xs bg-white text-secondary inline-block"
                                        href={`mailto:${salesChannel.email}`}
                                    >
                                        {salesChannel.email}
                                    </a>
                                </div>
                            )}
                            <div className="mt-5 text-sm opacity-90">
                                Customer Services Available
                                <br />
                                9AM - 5PM, Monday - Friday
                            </div>
                        </div>
                    </div>
                </div>
            </section>
            <section className="py-6">
                <div className="container max-w-screen-lg mx-auto">
                    <div className="flex flex-wrap md:justify-between items-center">
                        <div className="w-full md:w-1/3 px-3">
                            <div className="flex justify-center md:justify-start items-center">
                                <Link title="Home" to="/">
                                    <FooterLogo logoFile={logoFile} alt={salesChannel.name + " Logo"} />
                                </Link>
                            </div>
                        </div>
                        <div className="px-3 w-full items-center md:w-1/3">
                            <p className="text-center md:text-left mt-5 mb-3 md:mt-0 md:mb-0">
                                Copyright {new Date().getFullYear()}. All Rights Reserved.
                            </p>
                        </div>
                        <div className="w-full md:w-1/3 px-3 mt-3 md:mt-0">
                            {socials && socials.length > 0 && (
                                <div className="flex -mx-3 justify-center items-center">
                                    <div className="px-3">
                                        <h2 className="text-base text-secondary font-bold uppercase">
                                            Connect
                                        </h2>
                                    </div>

                                    <ul className="flex justify-center md:justify-start px-3 md:grow -mx-3">
                                        {socials.map(social => (
                                            <li key={social.id} className="px-3">
                                                <a
                                                    className="text-primary"
                                                    href={social.linkUrl}
                                                    target="_blank"
                                                    title={social.type}
                                                >
                                                    <SocialsIcon type={social.type} size={25} />
                                                </a>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </section>
        </footer>
    );
}

const FooterLogo = ({ logoFile, alt }: { logoFile?: { node: ImageDataLike }, alt: string }) => {

    if (logoFile) {
        const gatsbyImage = getImage(logoFile);
        if (gatsbyImage) {
            return <GatsbyImage
                image={gatsbyImage}
                alt={alt}
                className="max-h-[50px]"
                imgStyle={{
                    width: "auto"
                }}
            />;
        }
    }

    return <></>

}